import React, { useState, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Link } from 'react-router-dom';
import SVG from 'react-inlinesvg';
import ConfigContext from '../../../contexts/ConfigContext';
import {
  useClearAllNofiicationsMutation,
  useClearOneNotificationMutation,
  useGetNotificationsQuery,
} from '../../../redux/slices/notificationSlice';
import { sanitizeHtml } from '../../../modules/formatUtils';
import useAuth from '../../../hooks/useAuth';
import { stampFor, visualFor } from '../../vendor/truck/support/notificationDisplay';

// Components
import styles from './Notifications.module.postcss';
import Tooltip from '../../shared/Tooltip';
import TablerIcon from '../../catering/portal/TablerIcon';
import Ticon from '../../vendor/truck/schedule/Ticon';

/*
 * N1/N1b (design/notifications-v3.dc.html) — the panel that drops from the
 * bell. Four most recent rows, unread marked with wash + dot, the body
 * clamped to two lines, absolute stamps, and a footer that counts the rest
 * ("See all notifications · 5 more"). Rows link wherever the server says
 * (notification.url) — the old link-guessing per notifiable_type is gone.
 * The error state is N1b's shell with N2d's two lines inline (the 31 Aug
 * ruling — no separate frame): the same header, the card's title and body,
 * one retry, the footer kept.
 *
 * variant='plain' (default) — the public header's bare bell.
 * variant='boxed' — the vendor-chrome / host-portal 38x38 boxed bell. The
 * badge draws the UNREAD count (the dot-clearing number), not row count.
 *
 * The same panel mounts on the host portal's bell (H1) — pass settingsPath /
 * seeAllPath for the footer's destinations; the host's seeAllPath is
 * /catering/notifications, H2's page.
 */

const PANEL_CAP = 4;

function Notifications({ truck, variant = 'plain', seeAllPath, settingsPath }) {
  const boxed = variant === 'boxed';
  const { railsEnv } = useContext(ConfigContext);
  const { auth } = useAuth();

  const [open, setOpen] = useState(false);

  const { data, isLoading, isError, refetch } = useGetNotificationsQuery(
    { user_id: auth.userData.id, limit: PANEL_CAP },
    {
      pollingInterval: railsEnv === 'production' ? 30000 : 300000,
      skipPollingIfUnfocused: true,
    }
  );
  const [clearNotifications] = useClearAllNofiicationsMutation();
  const [clearOne] = useClearOneNotificationMutation();

  const rows = (data && data.notifications) || [];
  const total = (data && data.total) || 0;
  const unread = (data && data.unread) || 0;
  const moreCount = Math.max(total - rows.length, 0);
  // A failed background poll must not blank out rows we already have — the
  // error card is for having nothing to show (N2d).
  const showError = isError && !data;

  // Vendor mounts pass the truck; the footer destinations follow from it
  // unless given explicitly (the host portal passes its own).
  const resolvedSeeAll = seeAllPath
    || (truck ? `/vendor/truck/${truck.id}/notifications` : null);
  const resolvedSettings = settingsPath
    || (truck ? `/vendor/truck/${truck.id}/settings/notifications` : null);

  const handleClearNotifications = useCallback(async () => {
    try {
      await clearNotifications({ user_id: auth.userData.id }).unwrap();
    } catch (err) {
      console.error('Failed to clear Notifications: ', err);
    }
  }, [clearNotifications, auth.userData.id]);

  // Opening a row clears its dot; the row itself stays where it is.
  const markRead = (notification) => {
    if (notification.read_at) return;
    clearOne({ user_id: auth.userData.id, notification_ids: notification.id });
  };

  const renderRow = (notification) => {
    const visual = visualFor(notification);
    const isUnread = !notification.read_at;
    const body = notification.body || notification.message;
    const inner = (
      <React.Fragment>
        <span className={cn(styles.Tile, styles[`Tile_${visual.tile}`])}>
          <Ticon name={visual.icon} size={17} />
        </span>
        <span className={styles.RowText}>
          <div className={cn(styles.RowTitle, { [styles.RowTitle_unread]: isUnread })}>
            {notification.content}
          </div>
          { body &&
            <div
              className={styles.RowBody}
              // server-authored and sanitised before render
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(body) }}
            />
          }
          <div className={styles.RowStamp}>{stampFor(notification.created_at)}</div>
        </span>
        { isUnread && <span className={styles.Dot} /> }
      </React.Fragment>
    );

    if (notification.url) {
      return (
        <Link
          to={notification.url}
          key={notification.id}
          className={cn(styles.Row, { [styles.Row_unread]: isUnread })}
          onClick={() => markRead(notification)}
        >
          {inner}
        </Link>
      );
    }
    return (
      <div
        key={notification.id}
        className={cn(styles.Row, { [styles.Row_unread]: isUnread })}
      >
        {inner}
      </div>
    );
  };

  const skeletonRows = [55, 44, 60].map((width, i) => (
    <div key={i} className={styles.Row} aria-hidden='true'>
      <span className={styles.SkelTile} />
      <span className={styles.RowText}>
        <span className={styles.SkelLine} style={{ width: `${width}%` }} />
        <span className={cn(styles.SkelLine, styles.SkelLine_sub)} style={{ width: `${width + 20}%` }} />
      </span>
    </div>
  ));

  const panel = (
    <div className={styles.Panel}>
      <div className={styles.PanelHead}>
        <span className={styles.PanelTitle}>Notifications</span>
        { !isLoading && !showError && rows.length > 0 && unread > 0 &&
          <button
            type='button'
            className={styles.MarkAll}
            onClick={handleClearNotifications}
          >
            Mark all read
          </button>
        }
      </div>

      { isLoading && <div>{skeletonRows}</div> }

      { !isLoading && showError &&
        <div className={styles.Empty}>
          <div className={cn(styles.EmptyTile, styles.EmptyTile_error)}>
            <Ticon name='cloud-off' size={25} />
          </div>
          <div className={styles.EmptyTitle}>We couldn&#39;t load your notifications</div>
          <div className={styles.EmptyBody}>
            Nothing is lost. Check your connection and try again.
          </div>
          <button type='button' className={styles.TryBtn} onClick={() => refetch()}>
            Try again
          </button>
        </div>
      }

      { !isLoading && !showError && rows.length === 0 &&
        <div className={styles.Empty}>
          <div className={styles.EmptyTile}>
            <Ticon name='bell-check' size={25} />
          </div>
          <div className={styles.EmptyTitle}>You&#39;re all caught up</div>
          <div className={styles.EmptyBody}>
            Confirmations, quotes, and payouts will show up here.
          </div>
        </div>
      }

      { !isLoading && !showError && rows.length > 0 &&
        <div className={styles.Rows}>
          {rows.slice(0, PANEL_CAP).map(renderRow)}
        </div>
      }

      <div className={styles.Foot}>
        { resolvedSeeAll
          ? (
            <Link to={resolvedSeeAll} className={styles.FootLink}>
              See all notifications{ moreCount > 0 && ` · ${moreCount} more` }
            </Link>
          )
          : <span className={styles.FootSpacer} />
        }
        { resolvedSettings &&
          <Link to={resolvedSettings} className={styles.FootGear} aria-label='Notification settings'>
            <TablerIcon name='settings' size={15} />
          </Link>
        }
      </div>
    </div>
  );

  return (
    <Tooltip
      placement='bottom-end'
      triggerOnClick
      lightTheme
      hideArrow
      offsetValue={10}
      parentClassName={styles.BellButton_Wrapper}
      onShow={() => setOpen(true)}
      onHide={() => setOpen(false)}
      content={panel}
    >
      <button
        type='button'
        className={cn({
          [styles.BellButton]: !boxed,
          [styles.BellButton_active]: !boxed && open,
          [styles.BoxedBell]: boxed,
          [styles.BoxedBell_active]: boxed && open,
        })}
        aria-expanded={open}
        aria-label='Notifications'
        aria-haspopup='true'
      >
        { boxed
          ? <TablerIcon name='bell' size={18} />
          : <SVG src='/static/images/icons/bell.svg' width='24' height='24'/>
        }
        { unread > 0 &&
          <span className={boxed ? styles.Notif_CountBoxed : styles.Notif_Count}>
            {unread}
          </span>
        }
      </button>
    </Tooltip>
  );
}

Notifications.propTypes = {
  truck: PropTypes.object,
  variant: PropTypes.oneOf(['plain', 'boxed']),
  seeAllPath: PropTypes.string,
  settingsPath: PropTypes.string,
};

export default Notifications;
